import { createFileRoute, useParams } from '@tanstack/react-router'
import { postGraphqlMutation, type TAPIPlace } from '@/contexts/graphql-and-swr-apis'
import { useWSPlaceUpdates } from '@/hooks/useWSPlaceUpdates'
import { LargeSlideView } from '@/components/LargeSlideView'
import LatestEmbedOverlay from '@/components/LatestEmbedOverlay'
import LogicIf from '@/components/logic/LogicIf'

type LatestEmbedSearch = {
  align?: 'top' | 'bottom'
}

export const Route = createFileRoute('/embed/$placeIdOrCode/latest')({
  validateSearch: (search: Record<string, unknown>): LatestEmbedSearch => {
    return {
      align: (search.align as any) === 'bottom' ? 'bottom' : 'top',
    }
  },
  loader: async ({ params: { placeIdOrCode } }) => {
    return await postGraphqlMutation<{ place: TAPIPlace }>(`
      query ($placeId: String!) {
        place(id: $placeId) {
          id
          name
          address
          latitude
          longitude
          googlePlaceId
          place_code
        }
      }
    `, { placeId: placeIdOrCode });
  },
  component: LatestSlideEmbed,
})

function WaitingForSlidesPlaceholder() {
  return (
    <div className="bg-gray-800 w-full h-full flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gray-700 rounded-lg mx-auto mb-4 flex items-center justify-center">
          <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-bold mb-2">Waiting for Slides</h2>
        <p className="text-gray-400">No slides received yet</p>
      </div>
    </div>
  );
}

function LatestSlideEmbed() {
  const { placeIdOrCode } = useParams({ from: '/embed/$placeIdOrCode/latest' })
  const { align } = Route.useSearch()
  const { place } = Route.useLoaderData();
  const myPlace = place ?? {} as TAPIPlace;
  const placeId = myPlace.id ?? placeIdOrCode;

  const { slides, isConnected, isConnecting, isForbidden, connectionStatus } = useWSPlaceUpdates({
    placeId,
    enabled: true,
    since: 900,
  });

  const isLoading = isConnecting && slides.length === 0;
  const hasError = isForbidden || (connectionStatus === 'failed');
  const currentSlide = slides[0] ?? null;

  if (isLoading) {
    return (
      <div className="h-screen w-screen bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400">Connecting...</p>
        </div>
      </div>
    )
  }

  if (hasError && slides.length === 0) {
    return (
      <div className="h-screen w-screen bg-black flex items-center justify-center text-white p-4 text-center">
        <div>
          <h1 className="text-xl font-bold text-red-500 mb-2">
            {isForbidden ? 'Access Denied' : 'Connection Error'}
          </h1>
          <p className="text-gray-400 text-sm">Unable to connect to slides for {placeId}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen w-screen bg-black text-white overflow-hidden relative group">
      <div className="h-full w-full flex items-center justify-center overflow-hidden">
        <LogicIf condition={!!currentSlide}>
          <LargeSlideView slide={currentSlide!} full />
        </LogicIf>
        <LogicIf condition={!currentSlide}>
          <WaitingForSlidesPlaceholder />
        </LogicIf>
      </div>
      
      <LatestEmbedOverlay
        myPlace={myPlace}
        placeId={placeId}
        isConnected={isConnected}
        isLoading={isConnecting}
        slides={slides}
        align={align}
      />
      
    </div>
  )
}

